import type { PractitionerRole, Reference } from "@medplum/fhirtypes";

import type {
  ClinicPractitioner,
  PractitionerRolePatch,
  ServicePractitionerOption,
  ServicePractitionersChange,
} from "@/types/clinic-configuration";
import { FHIR_IDENTIFIER_SYSTEMS } from "@/constants/fhir.constants";
import { buildVersionedPatchOperations } from "@/lib/utils/clinic-configuration/resource-patch";
import { formatHumanName } from "@/lib/utils/fhir";
import { getIdentifierValues, getReferenceId } from "@/lib/utils/helpers/fhir";

/**
 * The `<Practitioner ID>:` part Kora reads the practitioner from. A role
 * without it is not a practitioner's role to Kora.
 */
const PRACTITIONER_ROLE_VALUE = /^[^:]+:/;

type RoleWithPractitioner = {
  role: PractitionerRole;
  practitionerId: string;
};

/**
 * The roles Kora reads a practitioner's services from: AKB's, with a
 * `<Practitioner ID>:` value, active or not, since Kora does not read `active`
 * here. The practitioner comes from `practitioner`: AKB writes it from the
 * same practitioner as the identifier. The clinic's `patient-coordinators` role
 * has no practitioner, so it is skipped.
 */
const toPractitionerRoles = (
  roles: PractitionerRole[]
): RoleWithPractitioner[] =>
  roles.flatMap((role) => {
    const practitionerId = getReferenceId(
      role.practitioner?.reference,
      "Practitioner"
    );
    const hasPractitionerValue = getIdentifierValues(
      [role],
      FHIR_IDENTIFIER_SYSTEMS.PRACTITIONER_ROLE
    ).some((value) => PRACTITIONER_ROLE_VALUE.test(value));

    return practitionerId && hasPractitionerValue
      ? [{ role, practitionerId }]
      : [];
  });

/**
 * One practitioner's roles as `toPractitionerRoles` reads them: AKB's,
 * active or not.
 */
export const getPractitionerRoles = (
  roles: PractitionerRole[],
  practitionerId: string
): PractitionerRole[] =>
  toPractitionerRoles(roles).flatMap(({ role, practitionerId: roleOwnerId }) =>
    roleOwnerId === practitionerId ? [role] : []
  );

export const toServiceId = ({ reference }: Reference): string | undefined =>
  getReferenceId(reference, "HealthcareService");

/**
 * Service id → the practitioners whose roles list it. Kora takes the union
 * over a practitioner's roles, so one role is enough. A service missing here is
 * open to any practitioner.
 */
export const groupServicePractitioners = (
  roles: PractitionerRole[]
): Record<string, string[]> => {
  const practitioners: Record<string, string[]> = {};

  for (const { role, practitionerId } of toPractitionerRoles(roles)) {
    for (const serviceId of (role.healthcareService ?? []).map(toServiceId)) {
      if (!serviceId) {
        continue;
      }

      const current = practitioners[serviceId] ?? [];
      if (!current.includes(practitionerId)) {
        practitioners[serviceId] = [...current, practitionerId];
      }
    }
  }

  return practitioners;
};

/**
 * `role` with `serviceId` added to or removed from its services, as a new
 * object, or `undefined` when the role already reads that way. Other
 * services stay as stored. FHIR has no empty arrays, so an empty list
 * removes the key.
 */
const setRoleService = (
  role: PractitionerRole,
  serviceId: string,
  isListed: boolean
): PractitionerRole | undefined => {
  const stored = role.healthcareService ?? [];
  const isStored = stored.some(
    (reference) => toServiceId(reference) === serviceId
  );

  if (isStored === isListed) {
    return undefined;
  }

  const healthcareService = isListed
    ? [...stored, { reference: `HealthcareService/${serviceId}` }]
    : stored.filter((reference) => toServiceId(reference) !== serviceId);
  const copy: PractitionerRole = { ...role, healthcareService };

  if (healthcareService.length === 0) {
    delete copy.healthcareService;
  }

  return copy;
};

/**
 * One patch per role the Service save changes. Only the practitioners added or
 * removed are touched, and **every** role of each, as AKB writes
 * the list: the same on all of a practitioner's roles. An inactive practitioner
 * is not on the card, so `some` leaves their roles as they are: their links
 * keep the service restricted. `any` removes every practitioner that lists the
 * service, inactive ones too. Each patch is built against the role the user
 * saw and tests its version, so a role written elsewhere since fails the
 * whole save.
 */
export const buildServicePractitionerRolePatches = (
  roles: PractitionerRole[],
  serviceId: string,
  change: ServicePractitionersChange,
  /**
   * The practitioners the card lists, from `practitionerList`.
   */
  activePractitionerIds: string[]
): PractitionerRolePatch[] => {
  const stored = new Set(groupServicePractitioners(roles)[serviceId] ?? []);
  const active = new Set(activePractitionerIds);
  const wanted = new Set(change.kind === "any" ? [] : change.practitionerIds);
  const added = new Set([...wanted].filter((id) => !stored.has(id)));
  const removed = new Set(
    [...stored].filter(
      (id) => !wanted.has(id) && (change.kind === "any" || active.has(id))
    )
  );

  return toPractitionerRoles(roles).flatMap(({ role, practitionerId }) => {
    const { id, meta } = role;
    if (!id || (!added.has(practitionerId) && !removed.has(practitionerId))) {
      return [];
    }

    const edited = setRoleService(role, serviceId, added.has(practitionerId));
    if (!edited) {
      return [];
    }
    if (!meta?.versionId) {
      throw new Error(`PractitionerRole "${id}" has no versionId.`);
    }

    return [
      {
        id,
        operations: buildVersionedPatchOperations(role, edited, meta.versionId),
      },
    ];
  });
};

/**
 * The locations an active role puts its practitioner at. An inactive role puts
 * them nowhere.
 */
const toActiveRoleLocationIds = (role: PractitionerRole): string[] =>
  role.active === false
    ? []
    : (role.location ?? []).flatMap(({ reference }) => {
        const locationId = getReferenceId(reference, "Location");
        return locationId ? [locationId] : [];
      });

/**
 * Where the practitioner works: the locations of their roles, without the
 * inactive ones. Kora books only through an active `Schedule`, and an
 * inactive role is how a removed location is meant to be kept.
 */
export const getPractitionerLocationIds = (
  roles: PractitionerRole[],
  practitionerId: string
): string[] => [
  ...new Set(
    toPractitionerRoles(roles).flatMap(
      ({ role, practitionerId: roleOwnerId }) =>
        roleOwnerId === practitionerId ? toActiveRoleLocationIds(role) : []
    )
  ),
];

/**
 * The practitioners a service can be restricted to, in the list's order: the
 * AKB practitioners, since Kora reads a practitioner from the role
 * identifier. A member or Scheduling-only practitioner has no AKB ID, so
 * no role can name them. `hasSites` uses the Practitioner drawer's rule
 * (`getPractitionerLocationIds`): a practitioner at no location cannot be
 * booked, so restricting a service to them would leave it unbookable.
 */
export const getServicePractitionerOptions = (
  practitioners: ClinicPractitioner[],
  roles: PractitionerRole[]
): ServicePractitionerOption[] => {
  const withSites = new Set(
    toPractitionerRoles(roles).flatMap(({ role, practitionerId }) =>
      toActiveRoleLocationIds(role).length > 0 ? [practitionerId] : []
    )
  );

  return practitioners.flatMap(({ practitioner, sources }) =>
    practitioner.id && sources.includes("akb")
      ? [
          {
            id: practitioner.id,
            label: formatHumanName(practitioner),
            hasSites: withSites.has(practitioner.id),
          },
        ]
      : []
  );
};
